import { useCallback, useEffect, useRef, useState } from 'react';
import { SELECTED_AUDIO_OUTPUT_KEY } from '../../constants';
import useDevices from '../../hooks/useDevices/useDevices';
export default function useActiveSinkId() {
	const { audioOutputDevices } = useDevices();
	const available = useRef(audioOutputDevices);
	available.current = audioOutputDevices;
	const [activeSinkId, setSink] = useState('default');
	const [sinkError, setSinkError] = useState<Error | null>(null);
	const generation = useRef(0);
	const active = useRef('default');
	const select = useCallback(async (sinkId: string) => {
		const attempt = ++generation.current;
		try {
			const probe = document.createElement('audio');
			if (probe.setSinkId) await probe.setSinkId(sinkId);
			else sinkId = 'default';
			if (attempt !== generation.current) return;
			if (
				sinkId !== 'default' &&
				!available.current.some((device) => device.deviceId === sinkId)
			)
				throw new Error('Speaker disconnected');
			active.current = sinkId;
			setSink(sinkId);
			localStorage.setItem(SELECTED_AUDIO_OUTPUT_KEY, sinkId);
			setSinkError(null);
		} catch {
			if (attempt === generation.current)
				setSinkError(
					new Error(
						'Unable to change speakers. Check your audio settings and try again.',
					),
				);
		}
	}, []);
	useEffect(() => {
		const stored = localStorage.getItem(SELECTED_AUDIO_OUTPUT_KEY);
		if (
			stored &&
			audioOutputDevices.some((device) => device.deviceId === stored)
		) {
			void select(stored);
		} else if (active.current !== 'default') {
			void select('default').then(() => {
				if (active.current === 'default')
					setSinkError(
						new Error(
							'Your speaker disconnected. Using the system speaker.',
						),
					);
			});
		}
	}, [audioOutputDevices, select]);
	useEffect(
		() => () => {
			generation.current++;
		},
		[],
	);
	return [activeSinkId, select, sinkError, setSinkError] as const;
}
